// src/admin/pages/compliance/vehicles/AdminComplianceDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  approveCompliance,
  cancelCompliance,
  forceVerifyCompliance,
  getComplianceDetail,
  requestComplianceCorrection,
  recomputeCompliance,
  overrideApproveCompliance,
  unlockReuploadCompliance,
  openAdminVehicleFile,
  downloadAdminVehicleFile,
} from "../../../../api/adminComplianceApi";

const DOC_ORDER = ["RC", "INSURANCE", "PERMIT", "FITNESS", "PUC"];
const PHOTO_ORDER = ["FRONT", "BACK"];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}
function safeStr(x) {
  return x == null ? "" : String(x);
}
function up(x) {
  return safeStr(x).trim().toUpperCase();
}

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return safeStr(v) || "—";
  return d.toLocaleDateString();
}

function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return safeStr(v) || "—";
  return d.toLocaleString();
}

function hasExistingFile(item) {
  return Boolean(item?.fileUrl);
}

function stateKey(item) {
  return String(item?.state || "").toUpperCase();
}

function isApprovedDoc(item) {
  return stateKey(item) === "VERIFIED";
}

function isAutoValid(item) {
  return stateKey(item) === "AUTO_VALID";
}

function isRejectedLike(item) {
  const s = stateKey(item);
  return s === "ACTION_REQUIRED" || s === "REJECTED" || s === "EXPIRED" || s === "INVALID";
}

function prettyState(state) {
  const s = String(state || "").toUpperCase();
  if (!s) return "Unknown";
  if (s === "AUTO_VALID") return "Not required";
  if (s === "ACTION_REQUIRED") return "Action required";
  return s.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());
}

function getStateTone(item) {
  if (isApprovedDoc(item)) return "good";
  if (isAutoValid(item)) return "neutral";
  if (isRejectedLike(item)) return "bad";
  if (hasExistingFile(item)) return "warn";
  return "neutral";
}

function getValidityTone(item) {
  if (!item?.validTill) return "neutral";
  if (item?.expired) return "bad";
  if (item?.expiringSoon) return "warn";
  return "good";
}

function buildPrimaryInfoChips(item) {
  if (!item) return [];

  const chips = [{ label: prettyState(item.state), tone: getStateTone(item) }];
  if (item.validTill) {
    chips.push({
      label: item.expired ? `Expired ${fmtDate(item.validTill)}` : `Valid till ${fmtDate(item.validTill)}`,
      tone: getValidityTone(item),
    });
  }
  return chips;
}

function buildIssueChips(item) {
  if (!item) return [];
  const chips = [];
  if (item.issueMessage && !isApprovedDoc(item) && !isAutoValid(item)) chips.push(item.issueMessage);
  if (item.expired) chips.push("Expired document");
  if (item.expiringSoon) chips.push("Expiring soon");
  return Array.from(new Set(chips.filter(Boolean)));
}

function buildDetailCells(item) {
  if (!item) return [];

  const cells = [];
  if (item.docNumber) cells.push({ label: "Number", value: item.docNumber });
  if (item.companyName) cells.push({ label: "Provider", value: item.companyName });
  if (item.permitType) cells.push({ label: "Permit type", value: item.permitType });
  if (item.issuingAuthority) cells.push({ label: "Authority", value: item.issuingAuthority });
  if (item.validFrom) cells.push({ label: "Valid from", value: fmtDate(item.validFrom) });
  if (item.validTill) cells.push({ label: "Valid till", value: fmtDate(item.validTill) });
  if (item.filename) cells.push({ label: "Filename", value: item.filename });
  if (item.uploadedAt) cells.push({ label: "Uploaded", value: fmtDateTime(item.uploadedAt) });
  if (item.lastCheckedAt) cells.push({ label: "Checked", value: fmtDateTime(item.lastCheckedAt) });
  if (item.processingStartedAt) cells.push({ label: "Processing started", value: fmtDateTime(item.processingStartedAt) });
  if (item.required != null) cells.push({ label: "Required", value: item.required ? "Yes" : "No" });
  if (item.autoValid != null) cells.push({ label: "Auto valid", value: item.autoValid ? "Yes" : "No" });
  if (item.optionalReason) cells.push({ label: "Optional reason", value: item.optionalReason });
  if (item.reuploadAllowed != null) cells.push({ label: "Reupload allowed", value: item.reuploadAllowed ? "Yes" : "No" });
  if (item.reuploadBlockReason) cells.push({ label: "Reupload block", value: item.reuploadBlockReason });
  return cells;
}

function Tag({ label, tone = "neutral", className = "" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <span
      className={classNames(
        "inline-flex items-center border px-2 py-1 text-[11px] font-bold uppercase tracking-wider leading-none rounded-md",
        cls,
        className
      )}
    >
      {label}
    </span>
  );
}

function StatusTag({ status }) {
  const s = up(status);
  const tone =
    s === "APPROVED_VERIFIED"
      ? "good"
      : s === "PENDING_ADMIN_REVIEW"
      ? "warn"
      : s === "ACTION_REQUIRED"
      ? "bad"
      : "neutral";
  return <Tag label={s || "—"} tone={tone} />;
}

function Button({ children, onClick, disabled, tone = "soft", className = "", title, type = "button" }) {
  const cls =
    tone === "danger"
      ? "border-rose-700 bg-rose-700 text-white hover:opacity-95"
      : tone === "dark"
      ? "border-slate-900 bg-slate-900 text-white hover:opacity-95"
      : tone === "ghost"
      ? "border-transparent bg-transparent text-slate-700 hover:bg-slate-50"
      : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50";

  return (
    <button
      type={type}
      title={title}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={classNames(
        "inline-flex h-10 items-center justify-center gap-2 border px-4 text-sm font-semibold rounded-md",
        "disabled:opacity-60 disabled:cursor-not-allowed",
        cls,
        className
      )}
    >
      {children}
    </button>
  );
}

function Section({ title, subtitle, right, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
          {subtitle ? <div className="mt-1 text-[13px] font-medium text-slate-500">{subtitle}</div> : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function KV({ k, v, hint, right }) {
  return (
    <div className="grid grid-cols-12 gap-3 py-3">
      <div className="col-span-12 md:col-span-4 text-[12px] font-bold uppercase tracking-wider text-slate-500">{k}</div>
      <div className="col-span-12 md:col-span-8 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="break-words text-sm font-semibold text-slate-900">{v ?? "—"}</div>
          {hint ? <div className="mt-1 text-[12px] font-medium text-slate-500">{hint}</div> : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
    </div>
  );
}

function ModalShell({ title, subtitle, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/35" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="text-lg font-extrabold tracking-tight text-slate-900">{title}</div>
            {subtitle ? <div className="mt-1 text-[13px] font-medium text-slate-500">{subtitle}</div> : null}
          </div>
          <div className="px-5 py-4">{children}</div>
          {footer ? <div className="border-t border-slate-200 px-5 py-4">{footer}</div> : null}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold text-slate-500">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function pickFileRef(it) {
  return it?.fileKey || it?.s3Key || it?.key || it?.fileUrl || it?.url || "";
}

export default function AdminComplianceDetail() {
  const { regNo } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [reason, setReason] = useState("");

  const [corrOpen, setCorrOpen] = useState(false);
  const [corr, setCorr] = useState({
    targetType: "DOC",
    docType: "RC",
    photoType: "FRONT",
    issueCode: "OTHER",
    message: "",
    clearFile: false,
  });

  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlock, setUnlock] = useState({
    targetType: "DOC",
    docType: "RC",
    photoType: "FRONT",
    clearFile: true,
    message: "Please re-upload a clear and correct file.",
  });

  const handle401 = (e) => {
    const status = e?.status || e?.response?.status;
    if (status === 401) {
      alert("Your admin session expired. Please login again.");
      return true;
    }
    return false;
  };

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const res = await getComplianceDetail(regNo);
      setData(res);
    } catch (e) {
      if (handle401(e)) {
        setErr("Session expired. Please login again.");
      } else {
        setErr(e?.message || "Failed to load compliance");
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  async function refreshAfterAction() {
    try {
      await load();
    } catch (e) {
      const status = e?.status || e?.response?.status;
      if (status === 401) {
        setErr("Session expired. Please login again.");
        return;
      }
      console.warn("Post-action refresh failed:", e);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regNo]);

  const dto = data?.compliance || null;
  const docs = useMemo(() => dto?.docs || {}, [dto]);
  const photos = useMemo(() => dto?.photos || {}, [dto]);

  const isVerified = Boolean(dto?.complianceVerified);
  const isComplete = Boolean(dto?.complianceComplete);

  // ✅ With new backend logic: complete can be true but verified false (pending review)
  const canOverrideApprove = isComplete && !isVerified;
  const canForceVerify = !isVerified;

  const busyTop = loading;

  const doMarkReviewed = async () => {
    if (!window.confirm("Mark as reviewed (audit only)?")) return;

    try {
      await approveCompliance(regNo, { reason });
    } catch (e) {
      if (handle401(e)) return;
      alert(e?.message || "Failed");
      return;
    }

    await refreshAfterAction();
  };

  const doRecompute = async () => {
    try {
      await recomputeCompliance(regNo);
    } catch (e) {
      if (handle401(e)) return;
      alert(e?.message || "Failed to recompute");
      return;
    }
    await refreshAfterAction();
  };

  const doOverrideApprove = async () => {
    if (!window.confirm("Override approve (manual verify)? Use only when documents are authentic.")) return;

    try {
      await overrideApproveCompliance(regNo, { reason });
    } catch (e) {
      if (handle401(e)) return;
      alert(e?.message || "Override approve failed");
      return;
    }
    await refreshAfterAction();
  };

  const doForceVerify = async () => {
    if (!window.confirm("Force verify this vehicle? This bypasses document checks and admin restrictions.")) return;

    try {
      await forceVerifyCompliance(regNo, { reason });
    } catch (e) {
      if (handle401(e)) return;
      alert(e?.message || "Force verify failed");
      return;
    }
    await refreshAfterAction();
  };

  const doCancel = async () => {
    if (!window.confirm("Cancel verification / move back to Action Required?")) return;

    try {
      await cancelCompliance(regNo, { reason });
    } catch (e) {
      if (handle401(e)) return;
      alert(e?.message || "Failed");
      return;
    }
    await refreshAfterAction();
  };

  const doRequestCorrection = async () => {
    const payload = { ...corr };
    const tt = String(payload.targetType || "").toUpperCase();
    payload.targetType = tt === "PHOTO" ? "PHOTO" : "DOC";

    if (payload.targetType === "DOC") {
      delete payload.photoType;
      payload.docType = payload.docType ? String(payload.docType).toUpperCase() : payload.docType;
      payload.clearFile = Boolean(payload.clearFile);
      if (payload.issueCode) payload.issueCode = String(payload.issueCode).toUpperCase();
    } else {
      delete payload.docType;
      payload.photoType = payload.photoType ? String(payload.photoType).toUpperCase() : payload.photoType;
      payload.clearFile = payload.clearFile !== false; // default true
      if (payload.issueCode) payload.issueCode = String(payload.issueCode).toUpperCase();
    }

    if (typeof payload.message === "string") payload.message = payload.message.trim();

    try {
      await requestComplianceCorrection(regNo, payload);
      setCorrOpen(false);
    } catch (e) {
      if (handle401(e)) return;
      alert(e?.message || "Failed to request correction");
      return;
    }

    await refreshAfterAction();
  };

  const doUnlock = async () => {
    const payload = { ...unlock };
    const tt = String(payload.targetType || "").toUpperCase();
    payload.targetType = tt === "PHOTO" ? "PHOTO" : "DOC";

    if (payload.targetType === "DOC") {
      delete payload.photoType;
      payload.docType = payload.docType ? String(payload.docType).toUpperCase() : payload.docType;
      payload.clearFile = Boolean(payload.clearFile);
    } else {
      delete payload.docType;
      payload.photoType = payload.photoType ? String(payload.photoType).toUpperCase() : payload.photoType;
      payload.clearFile = Boolean(payload.clearFile);
    }

    if (typeof payload.message === "string") payload.message = payload.message.trim();

    try {
      await unlockReuploadCompliance(regNo, payload);
      setUnlockOpen(false);
    } catch (e) {
      if (handle401(e)) return;
      alert(e?.message || "Failed to unlock reupload");
      return;
    }

    await refreshAfterAction();
  };

  return (
    <div className="min-w-0 space-y-4">
      <Section
        title="Compliance detail"
        subtitle="Review docs/photos, request correction, unlock reupload, or verify."
        right={
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <StatusTag status={dto?.status} />
            <Tag label={isComplete ? "COMPLETE" : "INCOMPLETE"} tone={isComplete ? "good" : "neutral"} />
            <Tag label={isVerified ? "VERIFIED" : "NOT VERIFIED"} tone={isVerified ? "good" : dto?.status === "PENDING_ADMIN_REVIEW" ? "warn" : "neutral"} />
          </div>
        }
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="text-2xl font-extrabold tracking-tight text-slate-900">{regNo}</div>
            <div className="mt-1 text-[13px] font-medium text-slate-500">
              Keep actions minimal here; use Support/Ops for other workflows.
            </div>

            {err ? (
              <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
                {err}
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2 justify-start lg:justify-end">
            <Button onClick={doRecompute} disabled={busyTop} title="Recompute compliance from stored files">
              Recompute
            </Button>
            <Button onClick={() => setUnlockOpen(true)} disabled={busyTop} title="Allow owner to reupload (optional clear file)">
              Unlock reupload
            </Button>
            <Button onClick={() => setCorrOpen(true)} disabled={busyTop} title="Ask owner to correct a specific doc/photo">
              Request correction
            </Button>
            <Button onClick={doMarkReviewed} disabled={busyTop} title="Audit only (does not verify)">
              Mark reviewed
            </Button>
            
            <Button
              onClick={doOverrideApprove}
              disabled={busyTop || !canOverrideApprove}
              tone="dark"
              title={!canOverrideApprove ? "Requires complianceComplete=true and not already verified" : "Manual verify"}
            >
              Override approve
            </Button>
            <Button
              onClick={doForceVerify}
              disabled={busyTop || !canForceVerify}
              tone="danger"
              title={!canForceVerify ? "Vehicle is already verified" : "Bypass document checks and force verification"}
            >
              Force verify
            </Button>
          </div>
        </div>
      </Section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Section title="Owner & Summary" subtitle="Primary contact plus top-level compliance timestamps and message." right={null}>
          <KV k="Name" v={data?.owner?.name || "—"} />
          <KV k="Phone" v={data?.owner?.phone || "—"} />
          <KV k="Email" v={data?.owner?.email || "—"} />
          <KV k="Message" v={dto?.message || "â€”"} />
          <KV k="Submitted At" v={dto?.submittedAt ? fmtDateTime(dto.submittedAt) : "â€”"} />
          <KV k="Verified At" v={dto?.verifiedAt ? fmtDateTime(dto.verifiedAt) : "â€”"} />
          <KV k="Updated At" v={dto?.updatedAt ? fmtDateTime(dto.updatedAt) : "â€”"} />
        </Section>

        <Section title="Compliance" subtitle="High-signal fields only." right={null}>
          <KV
            k="Status"
            v={
              <div className="flex flex-wrap items-center gap-2">
                <StatusTag status={dto?.status} />
              </div>
            }
          />
          <KV k="Complete" v={<Tag label={isComplete ? "YES" : "NO"} tone={isComplete ? "good" : "neutral"} />} />
          <KV k="Verified" v={<Tag label={isVerified ? "YES" : "NO"} tone={isVerified ? "good" : dto?.status === "PENDING_ADMIN_REVIEW" ? "warn" : "neutral"} />} />
          <KV k="Engine" v={dto?.engineNumber || "—"} />
          <KV k="Chassis" v={dto?.chassisNumber || "—"} />

       
         
        </Section>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="Documents" subtitle="State + issue + short message. Actions on right.">
          {loading ? (
            <div className="text-sm font-semibold text-slate-600">Loading…</div>
          ) : (
            <div className="space-y-2">
              {DOC_ORDER.map((k) => (
                <DocCard key={k} label={k} it={docs?.[k]} />
              ))}
            </div>
          )}
        </Section>

        <Section title="Photos" subtitle="Front + Back. Keep it quick.">
          {loading ? (
            <div className="text-sm font-semibold text-slate-600">Loading…</div>
          ) : (
            <div className="space-y-2">
              {PHOTO_ORDER.map((k) => (
                <PhotoCard key={k} label={k} it={photos?.[k]} />
              ))}
            </div>
          )}
        </Section>
      </div>

      {corrOpen ? (
        <ModalShell
          title="Request correction"
          subtitle="Pick one target and send a short, clear message."
          onClose={() => setCorrOpen(false)}
          footer={
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button onClick={() => setCorrOpen(false)}>Cancel</Button>
              <Button tone="dark" onClick={doRequestCorrection}>
                Send request
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Target">
                <select
                  value={corr.targetType}
                  onChange={(e) => setCorr((p) => ({ ...p, targetType: e.target.value }))}
                  className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50"
                >
                  <option value="DOC">Document</option>
                  <option value="PHOTO">Photo</option>
                </select>
              </Field>

              {corr.targetType === "DOC" ? (
                <Field label="Document type">
                  <select
                    value={corr.docType}
                    onChange={(e) => setCorr((p) => ({ ...p, docType: e.target.value }))}
                    className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50"
                  >
                    {DOC_ORDER.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Field label="Photo type">
                  <select
                    value={corr.photoType}
                    onChange={(e) => setCorr((p) => ({ ...p, photoType: e.target.value }))}
                    className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50"
                  >
                    {PHOTO_ORDER.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Issue code">
                <select
                  value={corr.issueCode}
                  onChange={(e) => setCorr((p) => ({ ...p, issueCode: e.target.value }))}
                  className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50"
                >
                  <option value="MISSING_FILE">MISSING_FILE</option>
                  <option value="UNREADABLE">UNREADABLE</option>
                  <option value="EXPIRED">EXPIRED</option>
                  <option value="REG_MISMATCH">REG_MISMATCH</option>
                  <option value="INVALID_FORMAT">INVALID_FORMAT</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </Field>

              <Field label="Clear file">
                <select
                  value={String(Boolean(corr.clearFile))}
                  onChange={(e) => setCorr((p) => ({ ...p, clearFile: e.target.value === "true" }))}
                  className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50"
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </Field>
            </div>

            <Field label="Message to owner (short & clear)">
              <textarea
                value={corr.message}
                onChange={(e) => setCorr((p) => ({ ...p, message: e.target.value }))}
                className="w-full resize-none rounded-md border border-slate-200 bg-white p-3 text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-300"
                rows={3}
                placeholder="Example: RC is unclear. Upload a clearer RC showing engine & chassis."
              />
            </Field>
          </div>
        </ModalShell>
      ) : null}

      {unlockOpen ? (
        <ModalShell
          title="Unlock reupload"
          subtitle="Allow owner to upload again (optionally clear current file)."
          onClose={() => setUnlockOpen(false)}
          footer={
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button onClick={() => setUnlockOpen(false)}>Cancel</Button>
              <Button tone="dark" onClick={doUnlock}>
                Unlock
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Target">
                <select
                  value={unlock.targetType}
                  onChange={(e) => setUnlock((p) => ({ ...p, targetType: e.target.value }))}
                  className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50"
                >
                  <option value="DOC">Document</option>
                  <option value="PHOTO">Photo</option>
                </select>
              </Field>

              {unlock.targetType === "DOC" ? (
                <Field label="Document type">
                  <select
                    value={unlock.docType}
                    onChange={(e) => setUnlock((p) => ({ ...p, docType: e.target.value }))}
                    className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50"
                  >
                    {DOC_ORDER.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Field label="Photo type">
                  <select
                    value={unlock.photoType}
                    onChange={(e) => setUnlock((p) => ({ ...p, photoType: e.target.value }))}
                    className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50"
                  >
                    {PHOTO_ORDER.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Clear file">
                <select
                  value={String(Boolean(unlock.clearFile))}
                  onChange={(e) => setUnlock((p) => ({ ...p, clearFile: e.target.value === "true" }))}
                  className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50"
                >
                  <option value="true">Yes (recommended)</option>
                  <option value="false">No</option>
                </select>
              </Field>
            </div>

            <Field label="Message to owner">
              <textarea
                value={unlock.message}
                onChange={(e) => setUnlock((p) => ({ ...p, message: e.target.value }))}
                className="w-full resize-none rounded-md border border-slate-200 bg-white p-3 text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-300"
                rows={3}
              />
            </Field>
          </div>
        </ModalShell>
      ) : null}
    </div>
  );
}

function toneForDocState(state) {
  const s = up(state);
  if (s === "AUTO_VALID") return "neutral";
  if (s === "VERIFIED") return "good";
  if (s === "UPLOADED") return "warn";
  if (s === "ACTION_REQUIRED" || s === "MISSING" || s === "EXPIRED" || s === "INVALID") return "bad";
  return "neutral";
}

function toneForIssue(issue) {
  const s = up(issue);
  if (!s || s === "NONE") return "neutral";
  if (s === "EXPIRED" || s === "REG_MISMATCH" || s === "INVALID_FORMAT") return "bad";
  return "warn";
}

function DetailGrid({ label, cells }) {
  if (!cells?.length) return null;
  return (
    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
      {cells.map((cell) => (
        <div key={`${label}-${cell.label}`} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{cell.label}</div>
          <div className="mt-1 break-words text-sm font-medium text-slate-800">{cell.value}</div>
        </div>
      ))}
    </div>
  );
}

function DocCard({ label, it }) {
  const state = up(it?.state || "MISSING");
  const issueCode = up(it?.issueCode || "");
  const infoChips = buildPrimaryInfoChips(it);
  const issueChips = buildIssueChips(it);
  const detailCells = buildDetailCells(it);

  const fileRef = pickFileRef(it);
  const hasFile = !!safeStr(fileRef).trim();

  const [busy, setBusy] = useState(false);

  const onOpen = async () => {
    if (!hasFile) return;
    try {
      setBusy(true);
      await openAdminVehicleFile(fileRef);
    } catch (e) {
      alert(e?.message || "Failed to open file");
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async () => {
    if (!hasFile) return;
    try {
      setBusy(true);
      await downloadAdminVehicleFile(fileRef);
    } catch (e) {
      alert(e?.message || "Failed to download file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-extrabold text-slate-900">{label}</div>
            <Tag label={state || "—"} tone={toneForDocState(state)} />
            {issueCode ? <Tag label={issueCode} tone={toneForIssue(issueCode)} /> : null}
            {infoChips
              .filter((chip) => safeStr(chip?.label).trim() && safeStr(chip.label).trim() !== prettyState(state))
              .map((chip) => (
                <Tag key={`${label}-${chip.label}`} label={chip.label} tone={chip.tone || "neutral"} />
              ))}
          </div>

          {issueChips.length ? (
            <div className="mt-3 space-y-2">
              {issueChips.map((msg) => (
                <div
                  key={`${label}-${msg}`}
                  className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] font-medium text-rose-700"
                >
                  {msg}
                </div>
              ))}
            </div>
          ) : null}
          <DetailGrid label={label} cells={detailCells} />
          {!hasFile ? <div className="mt-2 text-[13px] font-medium text-slate-500">No file attached.</div> : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button disabled={!hasFile || busy} onClick={onOpen} title={!hasFile ? "No file" : "Open file"}>
            {busy ? "…" : "Open"}
          </Button>
          <Button disabled={!hasFile || busy} onClick={onDownload} title={!hasFile ? "No file" : "Download file"}>
            {busy ? "…" : "Download"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PhotoCard({ label, it }) {
  const state = up(it?.state || "MISSING");
  const fileRef = pickFileRef(it);
  const hasFile = !!safeStr(fileRef).trim();
  const filename = safeStr(it?.filename || "").trim();
  const uploadedAt = it?.uploadedAt ? fmtDateTime(it.uploadedAt) : "";
  const reuploadAllowed = it?.reuploadAllowed;
  const reuploadBlockReason = safeStr(it?.reuploadBlockReason || "").trim();

  const [busy, setBusy] = useState(false);

  const tone =
    state === "UPLOADED" ? "warn" : state === "MISSING" ? "bad" : "neutral";

  const onOpen = async () => {
    if (!hasFile) return;
    try {
      setBusy(true);
      await openAdminVehicleFile(fileRef);
    } catch (e) {
      alert(e?.message || "Failed to open file");
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async () => {
    if (!hasFile) return;
    try {
      setBusy(true);
      await downloadAdminVehicleFile(fileRef);
    } catch (e) {
      alert(e?.message || "Failed to download file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-extrabold text-slate-900">{label}</div>
            <Tag label={state || "—"} tone={tone} />
          </div>
          <DetailGrid
            label={label}
            cells={[
              ...(filename ? [{ label: "Filename", value: filename }] : []),
              ...(uploadedAt ? [{ label: "Uploaded", value: uploadedAt }] : []),
              ...(reuploadAllowed != null ? [{ label: "Reupload allowed", value: reuploadAllowed ? "Yes" : "No" }] : []),
              ...(reuploadBlockReason ? [{ label: "Reupload block", value: reuploadBlockReason }] : []),
            ]}
          />
          {!hasFile ? <div className="mt-2 text-[13px] font-medium text-slate-500">No file attached.</div> : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button disabled={!hasFile || busy} onClick={onOpen} title={!hasFile ? "No file" : "Open file"}>
            {busy ? "…" : "Open"}
          </Button>
          <Button disabled={!hasFile || busy} onClick={onDownload} title={!hasFile ? "No file" : "Download file"}>
            {busy ? "…" : "Download"}
          </Button>
        </div>
      </div>
    </div>
  );
}
